import * as React from "react";
import { observer } from "mobx-react-lite";
import { useLocation, useNavigate } from "react-router-dom";
import { mk8sDryRun } from "../../../../mobxStores/dryRun/mk8s";
import { mk8sMobx } from "../../../../mst/kinds/mk8s/mk8s";
import { Mk8sDraftMobx } from "../../../../mst/stores/mk8s.draft";
import { Mk8sDraftProviderDigitalOceanMobx } from "../../../../mst/stores/mk8s.draft.provider.digitalocean";
import { homeLink, request } from "../../../../services/cpln";
import { DryRunAlert } from "../../dryRunAlert";
import { NGButton } from "../../../../newcomponents/button/Button";
import { NGFormElement } from "../../../../newcomponents/ngformelement/ngformelement";
import { MoreHorizontal } from "react-feather";
import { NGProviderBrowser } from "../../ngProviderBrowser";
import { NGSelect } from "../../../../newcomponents/select/ngselect";
import { NGInput } from "../../../../newcomponents/input/input";
import { NGInputListMst } from "../../../../newcomponents/inputList/inputListMst";
import { getDryRunErrorText } from "../../getDryRunErrorText";
import { DetectedTags, useHasDetectedTags } from "../../../../components/detail/detectedTags";
import { Instance } from "mobx-state-tree";
import { Mk8sDraftProviderDigitalOceanNodePoolModel } from "../../../../mst/stores/mk8s.draft.provider.digitalocean.nodePool";

interface Props {
  mk8s: mk8sMobx;
  mk8sDraft: Mk8sDraftMobx;
  digitalocean: Mk8sDraftProviderDigitalOceanMobx;
  nodePool: Instance<typeof Mk8sDraftProviderDigitalOceanNodePoolModel>;
  index: number;
}
const ProviderDigitalOceanNodePoolRaw: React.FC<Props> = ({ mk8sDraft, digitalocean: d, nodePool: np, index }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [browserKey, setBrowserKey] = React.useState("");

  const hasDetectedTags = useHasDetectedTags({
    value: np.labels.items,
    detectValue: (labels, key) => labels.some((l) => l.firstValue === key),
    detectionMap: {
      "cpln.io/nodeType": "if value is core: nodes can accept BYOK core components",
    },
  });

  const pre = `digitalocean.nodePools[${index}].`;

  const labelsDryRun = getDryRunErrorText({
    dryRunResponse: mk8sDryRun.response,
    path: `spec.provider.digitalocean.nodePools[${index}].labels`,
    paths: np.labels.items.map((i) => `spec.provider.digitalocean.nodePools[${index}].labels.${i.firstValue}`),
  });
  const taintsDryRun = getDryRunErrorText({
    dryRunResponse: mk8sDryRun.response,
    path: `spec.provider.digitalocean.nodePools[${index}].taints`,
    paths: [
      ...np.taints.items.map((_, idx) => `spec.provider.digitalocean.nodePools[${index}].taints[${idx}]`),
      ...np.taints.items.map((_, idx) => `spec.provider.digitalocean.nodePools[${index}].taints[${idx}].key`),
      ...np.taints.items.map((_, idx) => `spec.provider.digitalocean.nodePools[${index}].taints[${idx}].value`),
      ...np.taints.items.map((_, idx) => `spec.provider.digitalocean.nodePools[${index}].taints[${idx}].effect`),
    ],
  });

  return (
    <>
      <div>
        <NGFormElement
          name={`${pre}name`}
          label={np.name.label}
          value={np.name.value}
          onChange={np.name.setValue}
          required
          error={!d.isNodePoolNameValid(np.index) ? "Node pool names must be unique" : ""}
        />

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}dropletSize`}
            label={np.dropletSize.label}
            value={np.dropletSize.value}
            onChange={np.dropletSize.setValue}
            required={np.dropletSize.isRequired}
            error={np.dropletSize.error}
            innerButtons={[
              {
                title: "Browse",
                onClick: () => setBrowserKey("dropletSize"),
                render: () => <MoreHorizontal />,
              },
            ]}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={true}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.dropletSize.setValue}
              path={`spec.provider.digitalocean.nodePools[${index}].dropletSize`}
            />
          </div>
        </div>

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}overrideImage`}
            label={np.overrideImage.label}
            value={np.overrideImage.value}
            onChange={np.overrideImage.setValue}
            required={np.overrideImage.isRequired}
            error={np.overrideImage.error}
            innerButtons={[
              {
                title: "Browse",
                onClick: () => setBrowserKey("overrideImage"),
                render: () => <MoreHorizontal />,
              },
            ]}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={true}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.overrideImage.setValue}
              path={`spec.provider.digitalocean.nodePools[${index}].overrideImage`}
            />
          </div>
        </div>

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}minSize`}
            label={np.minSize.label}
            value={np.minSize.value}
            onChange={np.minSize.setValue}
            required={np.minSize.isRequired}
            error={np.minSize.error}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={true}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.minSize.setValue}
              path={`spec.provider.digitalocean.nodePools[${index}].minSize`}
            />
          </div>
        </div>

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}maxSize`}
            label={np.maxSize.label}
            value={np.maxSize.value}
            onChange={np.maxSize.setValue}
            required={np.maxSize.isRequired}
            error={np.maxSize.error}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={true}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.maxSize.setValue}
              path={`spec.provider.digitalocean.nodePools[${index}].maxSize`}
            />
          </div>
        </div>
        <NGInputListMst
          data={np.labels}
          className={hasDetectedTags ? "mb-2" : "mb-8"}
          label="Labels"
          labelRender={() => {
            return (
              <>
                {np.labels.items.some((l) => l.firstValue === "cpln.io/nodeType" && l.secondValue === "core") ? null : (
                  <NGButton
                    variant="primary"
                    className="ml-2"
                    onClick={() => {
                      const id = np.labels.add();
                      const item = np.labels.items.find((i) => i.id === id);
                      if (item) {
                        item.setFirstValue("cpln.io/nodeType");
                        item.setSecondValue("core");
                      }
                    }}
                    size={`small`}
                  >
                    Use this nodepool as BYOK core
                  </NGButton>
                )}
              </>
            );
          }}
          firstLabel="Key"
          firstInput={(i) => {
            let invalid = false;
            if (!i.firstValue) {
              invalid = true;
            }
            if (i.firstValue && labelsDryRun.message.includes(`.${i.firstValue}`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.firstValue}
                onChange={(e) => i.setFirstValue(e.target.value)}
              />
            );
          }}
          secondLabel="Value"
          secondInput={(i) => {
            let invalid = false;
            if (!i.secondValue) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.secondValue}
                onChange={(e) => i.setSecondValue(e.target.value)}
              />
            );
          }}
          invalid={labelsDryRun.severity === "error"}
          error={labelsDryRun.severity === "error" ? labelsDryRun.message : ""}
          warning={labelsDryRun.severity === "error" ? "" : labelsDryRun.message}
        />
        <DetectedTags
          className="mb-8"
          value={np.labels.items}
          detectValue={(labels, key) => labels.some((l) => l.firstValue === key)}
          detectionMap={{
            "cpln.io/nodeType": "if value is core: nodes can accept BYOK core components",
          }}
          pluralLabel="Labels"
        />
        <NGInputListMst
          data={np.taints}
          className="mb-8"
          label="Taints"
          firstLabel="Key"
          firstInput={(i, index) => {
            let invalid = false;
            if (!i.firstValue) {
              invalid = true;
            }
            if (i.firstValue && taintsDryRun.message.includes(`[${index}].key`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.firstValue}
                onChange={(e) => i.setFirstValue(e.target.value)}
              />
            );
          }}
          secondLabel="Value"
          secondInput={(i) => {
            let invalid = false;
            if (!i.secondValue) {
              invalid = true;
            }
            if (i.secondValue && taintsDryRun.message.includes(`[${index}].value`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.secondValue}
                onChange={(e) => i.setSecondValue(e.target.value)}
              />
            );
          }}
          thirdLabel="Effect"
          thirdInput={(i) => {
            let invalid = false;
            if (i.thirdValue && taintsDryRun.message.includes(`[${index}].effect`)) {
              invalid = true;
            }

            return (
              <NGSelect
                invalid={invalid}
                value={i.thirdValue}
                onChange={(value) => i.setThirdValue(value)}
                options={[
                  { label: "NoSchedule", value: "NoSchedule" },
                  { label: "PreferNoSchedule", value: "PreferNoSchedule" },
                  { label: "NoExecute", value: "NoExecute" },
                ]}
              />
            );
          }}
          invalid={taintsDryRun.severity === "error"}
          error={taintsDryRun.severity === "error" ? taintsDryRun.message : ""}
          warning={taintsDryRun.severity === "error" ? "" : taintsDryRun.message}
        />
        <NGButton
          style={{ width: 220 }}
          variant={"danger"}
          outlined
          onClick={() => {
            d.removeNodePoolAt(np.index);
            navigate(`${pathname.split("/-node-pools")[0]}/-node-pools`);
          }}
        >
          Delete Node Pool
        </NGButton>
      </div>
      {browserKey === "dropletSize" ? (
        <NGProviderBrowser
          title="Browse Digital Ocean Droplets"
          initialValue={np.dropletSize.value}
          fetchData={async () => {
            // TODOMK8S paginate
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "sizes",
                cluster: mk8sDraft.asObject,
              },
            });
            return data.sizes;
          }}
          sortable
          onOk={(value) => np.dropletSize.setValue(value as string)}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Availability", "vCPUs", "Memory", "Disk", "Cost per Month"]}
          rightAlign={["Cost per Month"]}
          distinctFilters={["vCPUs", "Memory", "Disk"]}
          tooltips={[
            {
              label: "Availability",
              content: `Shows if the size is available in selected region (${mk8sDraft.provider_digitalocean.region.value})`,
            },
          ]}
          getData={(item: any) => {
            let cost = "N/A";
            let price = 0;
            if (item.price_monthly) {
              price = item.price_monthly;
              cost = item.price_monthly.toLocaleString("en-US", { style: "currency", currency: "USD" });
            }

            return [
              //
              item.slug,
              item.slug,
              [item.available, item.available ? "Available" : "Not Available"],
              item.vcpus,
              [item.memory, `${item.memory}Gi`],
              [item.disk, `${item.disk}Gi`],
              [price, cost],
            ];
          }}
        />
      ) : null}
      {browserKey === "overrideImage" ? (
        <NGProviderBrowser
          title="Browse Digital Ocean Images"
          initialValue={np.overrideImage.value}
          fetchData={async () => {
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "images",
                cluster: mk8sDraft.asObject,
              },
            });
            return data.images.sort((a: any, b: any) => {
              if (a.slug > b.slug) {
                return 1;
              }
              if (a.slug < b.slug) {
                return -1;
              }
              return 0;
            });
          }}
          onOk={(value) => np.overrideImage.setValue(value as string)}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Availability", "Slug"]}
          tooltips={[
            {
              label: "Availability",
              content: `Shows if the image is available in selected region (${mk8sDraft.provider_digitalocean.region.value})`,
            },
          ]}
          getData={(item: any) => {
            const available = item.regions.includes(mk8sDraft.provider_digitalocean.region.value);

            return [item.slug, item.description, [available, available ? "Available" : "Not Available"], item.slug];
          }}
        />
      ) : null}
    </>
  );
};

export const ProviderDigitalOceanNodePool = observer(ProviderDigitalOceanNodePoolRaw);
